/**
 * ScenarioContextDef.ts
 *
 * Holds ONLY the context type definition and context object creation.
 * Kept separate from ScenarioContext.tsx (which contains ScenarioProvider component)
 * to avoid React Fast Refresh violations — Fast Refresh requires files to export
 * only components OR only non-component values, not both.
 */
import { createContext } from "react";
import type { Activity } from "../types/activity";
import {
	type ComprehensiveScenario,
	type ScenarioChange,
	type ScenarioCollection,
	ScenarioComment,
	type ScenarioLabel,
	type ScenarioPriority,
	type ScenarioShare,
	type ScenarioStatus,
} from "../types/scenarios";

export interface ScenarioContextType {
	// Data
	scenarios: ComprehensiveScenario[];
	collections: ScenarioCollection[];
	activities: Activity[];
	pendingApprovalCount: number;
	pendingApprovalScenarios: ComprehensiveScenario[];

	// Scenario CRUD
	createScenario: (
		scenario: Omit<ComprehensiveScenario, "id" | "createdAt" | "modifiedAt"> & {
			_dependencies?: { targetId: string; type: string }[];
		},
	) => Promise<string>;
	updateScenario: (
		id: string,
		updates: Partial<ComprehensiveScenario>,
	) => Promise<void>;
	deleteScenario: (id: string) => void;
	cloneScenario: (id: string) => Promise<void>;

	// Favorites
	toggleFavorite: (id: string) => void;

	// Priority & Status
	setPriority: (id: string, priority: ScenarioPriority) => void;
	setStatus: (id: string, status: ScenarioStatus) => void;
	setLabel: (id: string, label: ScenarioLabel) => void;

	// Changes
	addChange: (
		scenarioId: string,
		change: Omit<ScenarioChange, "id" | "createdAt" | "createdBy">,
	) => void;
	updateChange: (
		scenarioId: string,
		changeId: string,
		updates: Partial<ScenarioChange>,
	) => void;
	removeChange: (scenarioId: string, changeId: string) => void;

	// Comments
	addComment: (scenarioId: string, content: string, parentId?: string) => void;
	replyToComment: (
		scenarioId: string,
		commentId: string,
		content: string,
	) => void;
	reactToComment: (
		scenarioId: string,
		commentId: string,
		reaction: string,
	) => void;

	// Sharing
	shareScenario: (
		scenarioId: string,
		share: Omit<ScenarioShare, "sharedAt" | "sharedBy">,
	) => void;
	updateShare: (
		scenarioId: string,
		userId: string,
		permission: ScenarioShare["permission"],
	) => void;
	removeShare: (scenarioId: string, userId: string) => void;

	// Approval
	requestApproval: (
		scenarioId: string,
		approvers: string[],
		options?: { skipReview?: boolean; coveringNote?: string },
	) => Promise<string | undefined> | void;
	approveScenario: (scenarioId: string, comments?: string) => void;
	rejectScenario: (scenarioId: string, reason: string) => void;
	withdrawApproval: (scenarioId: string) => void;
	requestChanges: (scenarioId: string, feedback: string) => void;
	markReviewed: (scenarioId: string) => void;

	// Parking
	parkScenario: (id: string, reason: string, reviewDate: Date) => void;
	reactivateScenario: (id: string) => void;

	// Refresh — re-pin + re-materialise against the latest base forecast.
	// Resolves once the live refresh completes (void/no-op in demo or when the
	// scenario cannot be refreshed) so callers can chain follow-up refetches.
	refreshScenario: (id: string) => Promise<void> | void;

	// Collections
	createCollection: (name: string, description?: string) => void;
	addToCollection: (scenarioId: string, collectionId: string) => void;
	removeFromCollection: (scenarioId: string, collectionId: string) => void;

	// Tags
	addTag: (scenarioId: string, tag: string) => void;
	removeTag: (scenarioId: string, tag: string) => void;

	// Activities
	addActivity: (
		activity: Omit<Activity, "id" | "timestamp" | "isRead">,
	) => void;
	markActivityRead: (activityId: string) => void;
	markAllActivitiesRead: () => void;
	dismissActivity: (activityId: string) => void;

	// Versions
	createVersion: (scenarioId: string, description: string) => void;

	// Reset
	resetToDemo: () => void;

	// Current user (from persona switcher)
	currentUser: string;
	setCurrentUser: (userId: string) => void;
}

// Context object lives here — imported by ScenarioContext.tsx (Provider)
// and by hooks/useScenarios.ts (consumer). No circular dependency.
export const ScenarioContext = createContext<ScenarioContextType | undefined>(
	undefined,
);
